import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../services/auth';
import { useAuthStore } from '../store/auth';
import type { User } from '../models';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const { user, isAuthenticated, setAuth, setUser, clearAuth } = useAuthStore();

  // Verificar autenticação ao inicializar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = authService.getToken();
        
        // Se não há token, limpar tudo e marcar como não autenticado
        if (!token) {
          clearAuth();
          authService.clearAuth();
          setIsLoading(false);
          return;
        }

        // Se há token, verificar se é válido buscando dados do usuário
        try {
          const userData = await authService.getCurrentUser();
          
          // Tentar preservar "setor_logado" do localStorage se backend não retornar
          const storedUser = authService.getUser();
          const mergedUser = {
            ...userData,
            setor_logado: userData.setor_logado ?? storedUser?.setor_logado,
            setor_logado_fk: userData.setor_logado_fk ?? storedUser?.setor_logado_fk,
          } as User;
          
          setAuth(token, mergedUser);
        } catch (error: any) {
          // Se o token é inválido (erro 401), limpar tudo
          console.error('Erro ao validar token:', error);
          clearAuth();
          authService.clearAuth();
        }
      } catch (error) {
        // Erro inesperado, limpar estado
        console.error('Erro ao verificar autenticação:', error);
        clearAuth();
        authService.clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [setAuth, clearAuth]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const { token, user: userData } = await authService.login(email, password);
      setAuth(token, userData);
    } catch (error) {
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
    } finally {
      clearAuth();
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const userData = await authService.getCurrentUser(true);
      const storedUser = authService.getUser();
      const mergedUser = {
        ...userData,
        setor_logado: userData.setor_logado ?? storedUser?.setor_logado,
        setor_logado_fk: userData.setor_logado_fk ?? storedUser?.setor_logado_fk,
      } as User;
      setUser(mergedUser);
      // sincronizar também no authService/localStorage
      authService.saveUser(mergedUser);
    } catch (error) {
      // Se falhar ao buscar usuário, fazer logout
      logout();
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};